import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import TeacherCard from './components/TeacherCard';
import Footer from './components/Footer';
import { Teacher } from './types';
import { supabase } from './supabase';

const districts = [
    'Colombo', 'Gampaha', 'Kalutara', 'Kandy', 'Matale', 'Nuwara Eliya', 'Galle', 'Matara', 'Hambantota',
    'Jaffna', 'Kilinochchi', 'Mannar', 'Vavuniya', 'Mullaitivu', 'Batticaloa', 'Ampara', 'Trincomalee',
    'Kurunegala', 'Puttalam', 'Anuradhapura', 'Polonnaruwa', 'Badulla', 'Monaragala', 'Ratnapura', 'Kegalle'
];

export default function FindTeachersPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const aiIdsParam = searchParams.get('aiIds');
    const aiIds = useMemo(() => aiIdsParam ? aiIdsParam.split(',') : [], [aiIdsParam]);

    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const [selectedDistrict, setSelectedDistrict] = useState('');
    const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('Newest');

    // Fetch teachers from Supabase
    useEffect(() => {
        const fetchTeachers = async () => {
            try {
                setLoading(true);
                const { data, error } = await supabase.from('teachers').select('*').order('created_at', { ascending: false });
                if (error) throw error;
                const teachersData: Teacher[] = (data || []).map((row: any) => ({
                    id: row.id,
                    name: row.name,
                    subject: row.subject,
                    district: row.district,
                    bio: row.bio || `${row.subject} teacher in ${row.district}`,
                    mobile: row.mobile,
                    whatsapp: row.whatsapp,
                    medium: row.medium,
                    specificLocation: row.specific_location,
                    imageUrl: row.image_url,
                }));
                setTeachers(teachersData);
            } catch {
                // Silently fail
            } finally {
                setLoading(false);
            }
        };
        fetchTeachers();
    }, []);

    // Extract unique subjects from actual teacher data
    const allSubjects = useMemo(() => {
        const subjects = teachers.map(t => t.subject).filter(Boolean);
        return Array.from(new Set(subjects)).sort();
    }, [teachers]);

    const handleSubjectToggle = (subject: string) => {
        setSelectedSubjects(prev =>
            prev.includes(subject)
                ? prev.filter(s => s !== subject)
                : [...prev, subject]
        );
    };

    const clearFilters = () => {
        setSelectedDistrict('');
        setSelectedSubjects([]);
        if (aiIdsParam) {
            setSearchParams({});
        }
    };

    useEffect(() => {
        document.body.style.overflow = isMobileFiltersOpen ? 'hidden' : '';

        return () => {
            document.body.style.overflow = '';
        };
    }, [isMobileFiltersOpen]);

    const filteredTeachers = useMemo(() => {
        let results = teachers.filter(teacher => {
            if (aiIds.length > 0 && !aiIds.includes(teacher.id)) return false;
            if (selectedDistrict && teacher.district !== selectedDistrict) return false;
            if (selectedSubjects.length > 0 && !selectedSubjects.includes(teacher.subject)) return false;
            return true;
        });

        // Simple sorting for now
        if (sortBy === 'Name (A-Z)') {
            results.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortBy === 'Name (Z-A)') {
            results.sort((a, b) => b.name.localeCompare(a.name));
        }

        return results;
    }, [aiIds, teachers, selectedDistrict, selectedSubjects, sortBy]);

    const filterPanel = (
        <div className="bg-white rounded-3xl border border-slate-100 p-5 sm:p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-extrabold text-navy">Filters</h2>
                <button
                    onClick={clearFilters}
                    className="text-xs font-bold text-primary hover:underline"
                >
                    Clear All
                </button>
            </div>

            <div className="space-y-8">
                <div>
                    <h3 className="text-sm font-bold text-navy mb-4 uppercase tracking-wider">Available Subjects</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                        {allSubjects.length > 0 ? allSubjects.map(subject => (
                            <label key={subject} className="flex items-center gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={selectedSubjects.includes(subject)}
                                    onChange={() => handleSubjectToggle(subject)}
                                    className="w-5 h-5 rounded border-slate-200 text-primary focus:ring-primary/20"
                                />
                                <span className="text-sm font-medium text-slate-600 group-hover:text-navy">{subject}</span>
                            </label>
                        )) : (
                            <p className="text-xs text-slate-400 italic">No subjects found</p>
                        )}
                    </div>
                </div>

                <div>
                    <h3 className="text-sm font-bold text-navy mb-4 uppercase tracking-wider">Location</h3>
                    <select
                        id="filter-district"
                        name="districtFilter"
                        value={selectedDistrict}
                        onChange={(e) => setSelectedDistrict(e.target.value)}
                        className="w-full rounded-xl border-slate-200 bg-slate-50 text-sm font-medium focus:ring-primary/20 focus:border-primary px-4 py-3"
                    >
                        <option value="">All Locations</option>
                        {districts.map(d => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </select>
                </div>

                <div className="grid gap-3 sm:hidden pt-2 border-t border-slate-100">
                    <button
                        onClick={() => setIsMobileFiltersOpen(false)}
                        className="w-full rounded-2xl bg-primary text-white py-3 font-bold shadow-lg shadow-primary/20"
                    >
                        Show {filteredTeachers.length} Tutors
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="public-page-shell min-h-screen text-slate-900 antialiased">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12">
                <section className="public-hero-panel mb-8">
                    <div className="public-hero-content">
                        <div>
                            <p className="hero-eyebrow">Find Teachers</p>
                            <h1 className="public-hero-title">Discover teachers who feel credible before the first message.</h1>
                        </div>
                        <p className="public-hero-copy">
                            Explore qualified tutors across Sri Lanka, compare subject strength and teaching medium, and narrow the list quickly with a cleaner mobile-friendly search flow.
                        </p>
                        <div className="public-stat-grid">
                            <article className="public-stat-card">
                                <p className="public-stat-value">{loading ? 'Loading' : filteredTeachers.length}</p>
                                <p className="public-stat-label">Tutors visible with your current filters.</p>
                            </article>
                            <article className="public-stat-card">
                                <p className="public-stat-value">{allSubjects.length}</p>
                                <p className="public-stat-label">Subjects currently represented on the platform.</p>
                            </article>
                            <article className="public-stat-card">
                                <p className="public-stat-value">{selectedDistrict || 'All'}</p>
                                <p className="public-stat-label">Current location focus for your search.</p>
                            </article>
                        </div>
                    </div>
                </section>

                <nav aria-label="Breadcrumb" className="flex mb-8 text-sm font-medium text-slate-500">
                    <ol className="flex items-center space-x-2">
                        <li><Link to="/" className="hover:text-primary">Home</Link></li>
                        <li><span className="material-symbols-outlined text-base">chevron_right</span></li>
                        <li className="text-navy">Find Teachers</li>
                    </ol>
                </nav>

                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Sidebar Filters */}
                    <aside className="hidden lg:block lg:w-1/4">
                        <div className="sticky top-28">
                            {filterPanel}
                        </div>
                    </aside>

                    {/* Results Area */}
                    <div className="lg:w-3/4 space-y-6">
                        {/* Header Panel */}
                        {aiIds.length > 0 && (
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl border border-blue-100 p-4 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="material-symbols-outlined text-purple-600">auto_awesome</span>
                                    <span className="text-sm font-bold text-navy">Showing AI Smart Search Recommendations</span>
                                </div>
                                <button onClick={() => setSearchParams({})} className="text-xs font-bold text-slate-500 hover:text-slate-800">
                                    Clear AI Search &times;
                                </button>
                            </div>
                        )}
                        <div className="bg-white/90 rounded-3xl border border-slate-100 p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm backdrop-blur-sm">
                            <div>
                                <h2 className="text-xl font-extrabold text-navy">
                                    {loading ? 'Searching...' : `${filteredTeachers.length} Tutors Found`}
                                </h2>
                                <p className="text-slate-500 text-sm font-medium leading-6">
                                    {aiIds.length > 0 ? 'Based on your AI request' : selectedDistrict ? `In ${selectedDistrict}` : 'Across Sri Lanka'}
                                    {selectedSubjects.length > 0 ? ` • ${selectedSubjects.join(', ')}` : ''}
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
                                <button
                                    onClick={() => setIsMobileFiltersOpen(true)}
                                    className="lg:hidden inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-bold text-navy"
                                >
                                    <span className="material-symbols-outlined text-base">tune</span>
                                    Filters
                                </button>
                                <span className="text-sm font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">Sort By:</span>
                                <select
                                    id="sort-teachers"
                                    name="sortTeachers"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="w-full sm:w-auto rounded-xl border-slate-200 bg-slate-50 text-sm font-bold text-navy focus:ring-primary/20 focus:border-primary px-4 py-2"
                                >
                                    <option>Newest</option>
                                    <option>Name (A-Z)</option>
                                    <option>Name (Z-A)</option>
                                </select>
                            </div>
                        </div>

                        {/* Teacher List */}
                        <div className="space-y-4">
                            {loading ? (
                                // Skeleton Loaders
                                [1, 2, 3].map(i => (
                                    <div key={i} className="bg-white rounded-3xl border border-slate-50 p-4 sm:p-6 animate-pulse">
                                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                                            <div className="w-full sm:w-40 h-40 bg-slate-100 rounded-2xl"></div>
                                            <div className="flex-1 space-y-4 py-2">
                                                <div className="h-6 bg-slate-100 rounded w-2/3 sm:w-1/4"></div>
                                                <div className="h-4 bg-slate-100 rounded w-full sm:w-1/2"></div>
                                                <div className="h-4 bg-slate-100 rounded w-full sm:w-3/4"></div>
                                                <div className="h-10 bg-slate-100 rounded w-full sm:w-1/4 mt-auto"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : filteredTeachers.length > 0 ? (
                                filteredTeachers.map(teacher => (
                                    <TeacherCard key={teacher.id} teacher={teacher} />
                                ))
                            ) : (
                                <div className="bg-white rounded-3xl border border-slate-100 p-8 sm:p-12 text-center shadow-sm">
                                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                                        <span className="material-symbols-outlined text-4xl">person_search</span>
                                    </div>
                                    <h3 className="text-xl font-extrabold text-navy mb-2">No tutors found</h3>
                                    <p className="text-slate-500 max-w-sm mx-auto">
                                        We couldn't find any tutors matching your current filters. Try relaxing your search criteria.
                                    </p>
                                    <button
                                        onClick={clearFilters}
                                        className="mt-6 text-primary font-bold transition-all hover:scale-105"
                                    >
                                        Reset all filters
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Pagination Placeholder */}
                        {!loading && filteredTeachers.length > 5 && (
                            <div className="flex justify-center pt-8">
                                <nav className="flex items-center gap-2">
                                    <button className="w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-white hover:text-primary transition-all">
                                        <span className="material-symbols-outlined">chevron_left</span>
                                    </button>
                                    <button className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary text-white font-bold">1</button>
                                    <button className="w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-white hover:text-primary transition-all">
                                        <span className="material-symbols-outlined">chevron_right</span>
                                    </button>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            <Footer />

            {isMobileFiltersOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-sm transition-opacity lg:hidden"
                        onClick={() => setIsMobileFiltersOpen(false)}
                    />

                    <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pt-20 transition-transform duration-300 lg:hidden translate-y-0">
                        <div className="mx-auto max-w-lg rounded-[2rem] border border-white/70 bg-white/95 p-3 shadow-[0_-24px_60px_rgba(15,23,42,0.18)] backdrop-blur-xl">
                            <div className="mx-auto mb-3 h-1.5 w-14 rounded-full bg-slate-200" />
                            {filterPanel}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
